export type CaseStudy = {
  id: string;
  title: string;
  diagram?: boolean;
  attribution: string;
  problem: string;
  work: string;
  result: string;
};

export const caseStudyPath = (id: string) => `/case-studies/${id}`;

export const caseStudies: CaseStudy[] = [
  {
    id: 'multi-site-publishing',
    title: 'One publishing system, distinct sites',
    diagram: true,
    attribution: 'Delivered through our predecessor software practice',
    problem:
      'A network of independently branded sites needed separate content and navigation without maintaining a different publishing system for every domain.',
    work: 'Our principal designed and supported a PHP and MySQL CMS that shared one codebase across multiple branded domains. Site-specific configuration, reusable templates, dynamic menus, and role-based access supported each site. A multilingual editor helped non-technical staff prepare content in English, Spanish, and French.',
    result:
      'Staff could manage distinct sites through a common platform while maintaining their own content, navigation, and editorial access.',
  },
  {
    id: 'venue-workflow',
    title: 'Content and inquiries in one venue workflow',
    attribution: 'Delivered by Scaled Tech for The Garden Marquee',
    problem:
      'Venue staff needed to keep visitor-facing information current and handle event inquiries alongside availability.',
    work: 'Scaled Tech built a Next.js and PostgreSQL application with authenticated, role-based tools for staff to edit pages, posts, galleries, and FAQs. The visitor inquiry flow checks date availability and brings requests into an administrative workflow.',
    result:
      'Staff have a place to maintain venue content and review incoming event requests within the same application.',
  },
  {
    id: 'handbook-publishing',
    title: 'Publishing policy and handbook content',
    attribution: 'Earlier public-sector handbook work, client anonymous',
    problem:
      'Staff across departments needed a way to maintain and publish official policy and handbook content.',
    work: 'Our principal built a staff-only publishing application and API with single sign-on, policy and user-guide categories, validated document uploads, an audit log recording each change and its reason, full-text search with saved searches, and automated accessibility and performance testing.',
    result:
      'Staff could find current policies and see what was new or revised, and administrators kept a complete record of who changed what.',
  },
  {
    id: 'configurable-content',
    title: 'Configurable content and forms',
    attribution: 'Earlier custom content platform work',
    problem:
      'Administrators needed to organize structured content and forms without a code change for each layout.',
    work: 'Our principal developed a database-driven builder for reusable fields, field ordering, and multi-column content layouts.',
    result:
      'Administrators could configure fields and layouts through the platform instead of relying on a developer for each change.',
  },
  {
    id: 'staged-review',
    title: 'Multi-level approval for administrative briefings',
    attribution: 'Earlier public-sector work, client anonymous',
    problem:
      'Briefings for staff across a large organization had to clear department, division, and executive review before publication, with a faster path for urgent items.',
    work: 'Our principal built the platform on a Next.js interface and .NET API backed by SQL Server. Briefings move through three approval levels (department, division, and executive). Administrators set who may approve at each level and authors choose approvers for each item. Urgent items can start at the executive level, approvers can forward items upward, and declines return to the author with comments. Every transition sends email notifications and is recorded with revision history, and role-based permissions govern publishing, recall, restore, and reassignment.',
    result:
      'Each briefing had a defined, auditable path from draft to publication. Staff found published briefings through full-text search and audience filters, and administrators could see readership and approval turnaround.',
  },
];
